import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
// import { TChannel } from '@/features/channel-integration/_shared/types/channel.type';
import { GET_LIST_INTEGRATION_X_QUERY_KEY, TGetListIntegrationXResponse } from './get-list-integration-x.api';
import { TChannel } from '../../../_shared/types/channel.type';

/**
 * Delete Integration X Input Schema
 */
export const deleteIntegrationXValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteIntegrationXValidation = z.infer<typeof deleteIntegrationXValidationSchema>;
export type TDeleteIntegrationXPayload = TDeleteIntegrationXValidation;
export type TDeleteIntegrationXResponse = ApiResponse<TChannel>;

export const deleteIntegrationX = async (payload: TDeleteIntegrationXPayload): Promise<TDeleteIntegrationXResponse> => {
  const url = `/integration/twitter/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Integration X React-Query Hook
 */
type UseDeleteIntegrationXOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteIntegrationX, AxiosErrorCustom>;
};

export const useDeleteIntegrationX = ({ mutationConfig }: UseDeleteIntegrationXOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteIntegrationX,
    ...restConfig,
  });
};

/**
 * Use Delete Integration X Optimistically React-Query Hook
 */
type UseDeleteIntegrationXOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof deleteIntegrationX>;
};

type TDeleteIntegrationXOptimisticallyContext = {
  previousData: TGetListIntegrationXResponse | undefined;
};

export const useDeleteIntegrationXOptimistically = ({ mutationConfig }: UseDeleteIntegrationXOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteIntegrationX,
    onMutate: async (data: TDeleteIntegrationXValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_X_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListIntegrationXResponse>([GET_LIST_INTEGRATION_X_QUERY_KEY]);

      queryClient.setQueriesData<TGetListIntegrationXResponse>(
        { queryKey: [GET_LIST_INTEGRATION_X_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (integration) => integration.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteIntegrationXOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteIntegrationXOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_X_QUERY_KEY],
          (context as TDeleteIntegrationXOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
